import React, { useState } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";
import { Option } from "Components/AppGrid/shipeezi-new-grid";
import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

import { Currency } from "../../../../../models/currency";
import { Port } from "../../../../../models/port";
import { PurchaseOrderProduct } from "../../../../../models/purchase.order";
import { formatInTimeZone } from "../../../../../util/formatInTimezone";
import { formatCurrency, getPriceStringValid } from "../../../../../util/prices";
import AutoSuggest from "../../../../Form/AutoSuggest";
import Input from "../../../../Form/Input";
import Select from "../../../../Form/Select";
import Modal from "../../../../Generic/Modal";
import Skeleton from "../../../../Generic/Skeleton";
import getOrderOptions from "../../../getOrderOptions";

interface Props {
  handleClose: () => void;
  purchaseOrderProduct: PurchaseOrderProduct;
  editProduct: (product: PurchaseOrderProduct) => void;
  portCodes?: Port[];
  loadingPorts?: boolean;
  currenciesResponse?: UseQueryResult<Currency[], unknown>;
  viewOnly?: boolean;
  supplierView?: boolean;
}

const EditProduct = ({
  handleClose,
  purchaseOrderProduct,
  editProduct,
  portCodes,
  loadingPorts = true,
  currenciesResponse,
  viewOnly = false,
  supplierView = false,
}: Props) => {
  const { t: translate } = useTranslation();

  const [selectedOrderProduct, setSelectedOrderProduct] =
    useState<PurchaseOrderProduct>(purchaseOrderProduct);

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [rate, setRate] = useState("");

  const { modeOptions } = getOrderOptions();

  const dischargePortSelected = portCodes?.find(
    (port) => Number(selectedOrderProduct?.dischargePortId) === Number(port.id)
  );

  const getDateValue = (value = "") => {
    return formatInTimeZone("yyyy-MM-dd", Intl.DateTimeFormat().resolvedOptions().timeZone, value);
  };

  const decimalCase = Number(selectedOrderProduct?.currency?.decimalPoints || 2);

  const formatCurrencyToInput = (value?: string | number) => {
    if (!value || Number.isNaN(Number(value)))
      return formatCurrency(0, selectedOrderProduct?.currency, false);

    return formatCurrency(
      Number(value).toFixed(decimalCase),
      selectedOrderProduct?.currency,
      false
    );
  };

  const onChangeRateAndMargin = () => {
    let replaced = getPriceStringValid(rate, selectedOrderProduct?.currency?.decimalSeparator);
    const nValue = Number(replaced);

    if (Number.isNaN(nValue)) replaced = "0";

    setSelectedOrderProduct({
      ...selectedOrderProduct,
      purchasePrice: Number(replaced),
    });
  };

  return (
    <Modal
      open={true}
      handleClose={handleClose}
      title={`${viewOnly ? translate("viewProduct") : translate("editProduct")} - ${
        selectedOrderProduct?.product?.sku
      }`}
      showActionButtons
      actionButtonProps={{
        onSave: () => editProduct(selectedOrderProduct),
        onCancel: () => handleClose(),
        saveDisabled: viewOnly,
      }}
    >
      <Grid container direction="row" alignItems="center" justifyContent="flex-start" spacing={2}>
        <Grid item xs={3}>
          <Input
            disabled
            name="product-description"
            label={translate("productName")}
            value={selectedOrderProduct?.description || selectedOrderProduct?.product?.description}
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            disabled
            name="product-description"
            label={translate("styleCode")}
            value={selectedOrderProduct?.product.style}
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            type="number"
            min="0"
            name="quantity-requested"
            label={translate("quantityRequested")}
            value={String(selectedOrderProduct?.quantity)}
            onChange={(event) =>
              setSelectedOrderProduct({
                ...selectedOrderProduct,
                quantity: Number(event.target.value),
              })
            }
            disabled={viewOnly || supplierView}
          />
        </Grid>
        <Grid item xs={3}>
          <Skeleton isInput isLoading={!!currenciesResponse?.isFetching}>
            <Select
              data-cy="select-currency-id"
              name="currency"
              defaultValue=""
              displayEmpty
              placeholder={translate("currency")}
              label={translate("currency")}
              value={selectedOrderProduct?.currencyId}
              onChange={(event) =>
                setSelectedOrderProduct({
                  ...selectedOrderProduct,
                  currencyId: Number(event.target.value),
                })
              }
              disabled={viewOnly || supplierView}
            >
              {currenciesResponse?.data &&
                currenciesResponse?.data.map((currency: Currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {currency.code}
                  </MenuItem>
                ))}
            </Select>
          </Skeleton>
        </Grid>
        <Grid item xs={3}>
          <Select
            data-cy="select-mode"
            name="mode"
            defaultValue=""
            displayEmpty
            placeholder={translate("mode")}
            label={translate("mode")}
            value={selectedOrderProduct?.mode}
            onChange={(event: SelectChangeEvent<any>) =>
              setSelectedOrderProduct({
                ...selectedOrderProduct,
                mode: event.target.value,
              })
            }
            disabled={viewOnly || supplierView}
          >
            {modeOptions &&
              modeOptions.map((status: Option) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
          </Select>
        </Grid>
        <Grid item xs={3}>
          <Skeleton isInput isLoading={loadingPorts}>
            <AutoSuggest
              name="discharge-port"
              placeholder={translate("dischargePort")}
              label={translate("dischargePort")}
              inputValue={dischargePortSelected?.siteCode || ""}
              options={portCodes || []}
              getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
              getOptionLabel={(port: Port) => port.siteCode || ""}
              disabled={viewOnly || supplierView}
              onChange={(port: Port) => {
                if (port && port.id) {
                  setSelectedOrderProduct({
                    ...selectedOrderProduct,
                    dischargePortId: Number(port.id),
                  });
                } else {
                  setSelectedOrderProduct({
                    ...selectedOrderProduct,
                    dischargePortId: undefined,
                  });
                }
              }}
            />
          </Skeleton>
        </Grid>
        <Grid item xs={3}>
          <Input
            name="purchase-price"
            label={translate("unitPrice")}
            value={isFocused ? rate : formatCurrencyToInput(selectedOrderProduct?.purchasePrice)}
            onFocus={() => {
              setRate(String(formatCurrencyToInput(selectedOrderProduct?.purchasePrice)));
              setIsFocused(true);
            }}
            onChange={(e) => {
              setRate(e.target.value);
            }}
            onBlur={() => {
              onChangeRateAndMargin();
              setRate("");
              setIsFocused(false);
            }}
            placeholder={"0.00"}
            leftIcon={selectedOrderProduct?.currency?.symbol || "$"}
            rightIcon={selectedOrderProduct?.currency?.code}
            inputTextAlign={"start"}
            disabled={viewOnly || supplierView}
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            type="date"
            name="expected-start-date"
            label={translate("expectedStartDate")}
            value={getDateValue(selectedOrderProduct?.expectedStartDate)}
            disabled={viewOnly || supplierView}
            onChange={(event) =>
              setSelectedOrderProduct({
                ...selectedOrderProduct,
                expectedStartDate: formatInTimeZone(
                  "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
                  "UTC",
                  parseISO(event.target.value)
                ),
              })
            }
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            type="date"
            name="expected-end-date"
            disabled={viewOnly || supplierView}
            label={translate("expectedEndDate")}
            value={getDateValue(selectedOrderProduct?.expectedEndDate)}
            onChange={(event) =>
              setSelectedOrderProduct({
                ...selectedOrderProduct,
                expectedEndDate: formatInTimeZone(
                  "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
                  "UTC",
                  parseISO(event.target.value)
                ),
              })
            }
          />
        </Grid>
      </Grid>
    </Modal>
  );
};

export default EditProduct;
